import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import api from "@/lib/api";
import { buildOption, applyLegendOptions } from "@/lib/chartTheme";
import { buildSemanticQuery } from "@/lib/widgetCatalog";
import { applyChartSort } from "@/lib/chartData";
import { Loader2, Inbox, AlertTriangle } from "lucide-react";

// Item 10 — one Pie/Doughnut widget can render an exact Row × Column grid of charts.
// Every instance owns its Row (grouping), Column (slice category) and Value (measure)
// assignment plus its own formatting, so editing one chart never touches another.
// A 1 × 1 grid (or no grid config at all) keeps the classic single-chart path.

export const clampGrid = (n) => Math.max(1, Math.min(4, Number(n) || 1));

export function gridSize(config) {
  const g = (config && config.chart_grid) || {};
  return { rows: clampGrid(g.rows), cols: clampGrid(g.cols) };
}

export function isChartGrid(type, config) {
  if (!["pie", "donut", "semi_donut"].includes(type)) return false;
  const { rows, cols } = gridSize(config);
  return rows * cols > 1;
}

// Instance list, padded to the grid size; the first instance falls back to the widget's own wells.
export function gridInstances(config, count) {
  const list = Array.isArray(config.chart_instances) ? config.chart_instances : [];
  const d0 = (config.dimensions || [])[0] || {};
  const d1 = (config.dimensions || [])[1] || {};
  const m0 = (config.measures || [])[0] || {};
  const fallback = {
    row: d1.field || null,
    column: d0.field || null,
    value: m0.field ? { field: m0.field, agg: m0.agg || "sum" } : null,
    options: {},
  };
  const out = [];
  for (let i = 0; i < count; i++) {
    const inst = list[i] || (i === 0 ? fallback : {});
    out.push({
      row: inst.row || null,
      column: inst.column || null,
      value: inst.value || null,
      title: inst.title || "",
      filters: inst.filters || [],
      options: inst.options || {},
    });
  }
  return out;
}

function instConfig(widget, inst, cells = 1) {
  const dims = [];
  if (inst.row) dims.push({ field: inst.row, date_part: "none" });
  if (inst.column) dims.push({ field: inst.column, date_part: "none" });
  // Item 10: inside a grid each chart is small, so start compact (user settings still win)
  const compact = cells > 1
    ? { legendMode: "hide", pieLabelMode: "percent", pieLabelPos: "inside", pieLabelFontSize: 9, pieBorderWidth: 1 }
    : {};
  return {
    dimensions: dims,
    measures: inst.value?.field ? [{ field: inst.value.field, agg: inst.value.agg || "sum" }] : [],
    filters: [...((widget.config || {}).filters || []), ...(inst.filters || [])],
    options: { ...compact, ...((widget.config || {}).options || {}), ...(inst.options || {}) },
  };
}

function useInstanceData({ datasetId, cfg, filters, publicToken, refreshToken }) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const qKey = JSON.stringify({ cfg, filters, datasetId, publicToken, refreshToken });
  useEffect(() => {
    let active = true;
    if (!datasetId || !cfg.measures.length || !cfg.dimensions.length) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const semQuery = buildSemanticQuery(cfg, filters);
    setState((s) => ({ ...s, loading: true, error: null }));
    const req = publicToken
      ? api.post("/public/model/query", { token: publicToken, dataset_id: datasetId, semantic_query: semQuery }, { timeout: 45000 })
      : api.post(`/datasets/${datasetId}/model/query`, semQuery, { timeout: 45000 });
    req.then(({ data }) => { if (active) setState({ data, loading: false, error: null }); })
      .catch((e) => {
        if (!active) return;
        const detail = e?.response?.data?.detail;
        setState({ data: null, loading: false, error: typeof detail === "string" ? detail : "Failed to load data" });
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qKey]);
  return state;
}

function ChartInstance({ widget, type, inst, index, cells, filters, publicToken, refreshToken }) {
  const cfg = useMemo(() => instConfig(widget, inst, cells), [widget, inst, cells]);
  const { data, loading, error } = useInstanceData({
    datasetId: widget.dataset_id, cfg, filters, publicToken, refreshToken,
  });
  const ready = data && data.rows && data.rows.length;
  const option = useMemo(() => {
    if (!ready) return null;
    const colKey = cfg.dimensions.length > 1 ? cfg.dimensions[1] : cfg.dimensions[0];
    const rowKey = cfg.dimensions.length > 1 ? cfg.dimensions[0] : null;
    const xKey = colKey?.field;
    const yKeys = cfg.measures.map((m) => `${m.agg || "sum"}_${m.field}`);
    const spec = { chart_type: type, x_axis: xKey, y_axis: yKeys, series: rowKey?.field || null };
    const sorted = applyChartSort(data, spec, cfg.options);
    const opt = buildOption(type, sorted, spec, cfg.options);
    applyLegendOptions(opt, { legendPos: cfg.options.legendPos, legendFontSize: cfg.options.legendFontSize, legendColor: cfg.options.legendColor });
    // Row assignment groups the slices: inner ring = row totals, outer ring = the column slices.
    if (rowKey?.field && Array.isArray(opt.series) && opt.series[0]) {
      const totals = new Map();
      sorted.rows.forEach((r) => {
        const g = String(r[rowKey.field] ?? "—");
        totals.set(g, (totals.get(g) || 0) + (Number(r[yKeys[0]]) || 0));
      });
      const outer = opt.series[0];
      const innerR = type === "pie" ? ["0%", "32%"] : ["0%", "28%"];
      const innerData = [...totals.entries()].map(([name, value]) => ({ name, value }));
      opt.series = [
        {
          type: "pie", radius: innerR,
          label: { show: innerData.length <= 6, position: "inner", fontSize: 9, formatter: "{b}" },
          labelLine: { show: false }, emphasis: { scale: false }, labelLayout: { hideOverlap: true },
          data: innerData,
        },
        { ...outer, radius: type === "pie" ? ["42%", "70%"] : ["46%", "72%"] },
      ];
    }
    return opt;
  }, [ready, data, cfg, type]);

  const title = inst.title || inst.column || "";
  return (
    <div data-testid={`chart-grid-cell-${index}`}
      className="min-w-0 min-h-0 flex flex-col overflow-hidden rounded-[var(--radius-control)] border border-[color:var(--glass-border)] bg-[color:var(--surface)]">
      {title && <div data-testid={`chart-grid-title-${index}`} className="px-2 pt-1 text-[10px] font-semibold text-[color:var(--ink-2)] truncate shrink-0">{title}</div>}
      <div className="flex-1 min-h-0">
        {loading && !data ? (
          <div className="h-full w-full flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-[color:var(--brand-ocean)]" /></div>
        ) : error ? (
          <div className="h-full w-full flex items-center justify-center text-center px-2 text-[10px] text-[color:var(--danger)]">
            <span><AlertTriangle className="w-3.5 h-3.5 mx-auto mb-0.5" />{error}</span>
          </div>
        ) : !cfg.measures.length || !cfg.dimensions.length ? (
          <div data-testid={`chart-grid-unbound-${index}`} className="h-full w-full flex items-center justify-center text-center px-2 text-[10px] text-[color:var(--ink-3)]">
            Assign a Column (slice) and a Value for chart {index + 1}
          </div>
        ) : !ready ? (
          <div className="h-full w-full flex items-center justify-center text-[10px] text-[color:var(--ink-3)]"><Inbox className="w-3.5 h-3.5 mr-1 opacity-60" />No data</div>
        ) : (
          <ReactECharts option={option} style={{ height: "100%", width: "100%", minHeight: 80 }} opts={{ renderer: "svg" }} notMerge />
        )}
      </div>
    </div>
  );
}

export function ChartGridView({ widget, type, filters = [], publicToken = null, refreshToken = 0 }) {
  const config = widget.config || {};
  const { rows, cols } = gridSize(config);
  const instances = gridInstances(config, rows * cols);
  return (
    <div data-testid="chart-grid" data-rows={rows} data-cols={cols}
      className="h-full w-full grid gap-1.5 p-1 overflow-hidden"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}>
      {instances.map((inst, i) => (
        <ChartInstance key={i} widget={widget} type={type} inst={inst} index={i} cells={rows * cols}
          filters={filters} publicToken={publicToken} refreshToken={refreshToken} />
      ))}
    </div>
  );
}
